import React, { useEffect, useState, useMemo } from 'react';
import { permissionsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ShieldCheck, RotateCcw, Save, Lock, Eye, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = {
  owner_admin: { label: 'Owner Admin', desc: 'Top tier — full access incl. billing & user management', locked: true },
  admin: { label: 'Admin', desc: 'Full operational access' },
  manager: { label: 'Manager', desc: 'Mid-tier ops manager (no destructive financial deletes)' },
  dispatcher: { label: 'Dispatcher', desc: 'Daily operations: loads, schedule, drivers' },
  staff: { label: 'Staff', desc: 'Office staff — limited modules' },
  driver: { label: 'Driver', desc: 'Vehicle driver — own data only' },
  owner_operator: { label: 'Owner-Operator', desc: 'Contracted truck owner — own loads & settlements' },
  viewer: { label: 'Viewer', desc: 'Read-only access' },
};

const MODULE_LABELS = {
  dashboard: 'Dashboard',
  summary: 'Weekly Summary',
  loads: 'Weekly Loads',
  deductions: 'Deductions',
  recurring_deductions: 'Recurring Deductions',
  history: 'History',
  drivers: 'Drivers',
  staff: 'Staff',
  owner_operators: 'Owner Operators',
  schedule: 'Schedule',
  units: 'Units',
  vehicle_management: 'Vehicle Mgmt',
  driver_files: 'Driver Files',
  accounting: 'Accounting',
  expenses: 'Expenses',
};

const ACTION_ICONS = {
  view: { icon: Eye, label: 'View', color: 'text-blue-600' },
  create: { icon: Plus, label: 'Create', color: 'text-green-600' },
  edit: { icon: Pencil, label: 'Edit', color: 'text-amber-600' },
  delete: { icon: Trash2, label: 'Delete', color: 'text-red-600' },
};

export default function RolesPermissionsTab() {
  const [data, setData] = useState(null);
  const [selectedRole, setSelectedRole] = useState('admin');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const res = await permissionsApi.getRoles();
      setData(res.data);
      setDirty(false);
    } catch {
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (role, module, action) => {
    if (ROLE_LABELS[role]?.locked) return;
    setData(prev => {
      const next = { ...prev };
      next.permissions = { ...prev.permissions };
      next.permissions[role] = { ...prev.permissions[role] };
      next.permissions[role][module] = {
        ...prev.permissions[role][module],
        [action]: !prev.permissions[role][module]?.[action],
      };
      // Cascade: if you turn OFF view, turn off everything below
      if (action === 'view' && !next.permissions[role][module][action]) {
        ['create', 'edit', 'delete'].forEach(a => { next.permissions[role][module][a] = false; });
      }
      // If you turn ON create/edit/delete, ensure view is on
      if (action !== 'view' && next.permissions[role][module][action]) {
        next.permissions[role][module].view = true;
      }
      return next;
    });
    setDirty(true);
  };

  const toggleEntireRow = (role, module, allOn) => {
    if (ROLE_LABELS[role]?.locked) return;
    setData(prev => {
      const next = { ...prev };
      next.permissions = { ...prev.permissions };
      next.permissions[role] = { ...prev.permissions[role] };
      next.permissions[role][module] = {
        view: allOn, create: allOn, edit: allOn, delete: allOn,
      };
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await permissionsApi.updateRoles(data.permissions);
      toast.success('Permissions saved');
      setDirty(false);
    } catch {
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all role permissions back to platform defaults? This cannot be undone.')) return;
    setSaving(true);
    try {
      await permissionsApi.resetRoles();
      await load();
      toast.success('Permissions reset to defaults');
    } catch {
      toast.error('Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  const rolePerms = useMemo(() => data?.permissions?.[selectedRole] || {}, [data, selectedRole]);
  const isLocked = ROLE_LABELS[selectedRole]?.locked;

  if (loading) return (
    <Card><CardContent className="p-6 space-y-3">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-64 w-full" />
    </CardContent></Card>
  );

  return (
    <Card data-testid="roles-permissions-tab">
      <CardHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2 font-manrope">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
            Roles & Permissions
          </CardTitle>
          <CardDescription>Fine-tune what each role can see and do across the app.</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleReset} disabled={saving} data-testid="reset-permissions-btn">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />Reset
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!dirty || saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-permissions-btn">
            <Save className="h-3.5 w-3.5 mr-1.5" />{saving ? 'Saving…' : dirty ? 'Save Changes' : 'Saved'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Role selector */}
        <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border bg-muted/30">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Role</label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger className="mt-1" data-testid="role-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {data?.roles?.map(r => (
                  <SelectItem key={r} value={r} data-testid={`role-option-${r}`}>
                    <div className="flex items-center gap-2">
                      <span>{ROLE_LABELS[r]?.label || r}</span>
                      {ROLE_LABELS[r]?.locked && <Lock className="h-3 w-3 text-muted-foreground" />}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-[2] min-w-[200px]">
            <p className="text-sm text-muted-foreground">{ROLE_LABELS[selectedRole]?.desc}</p>
            {isLocked && (
              <Badge variant="secondary" className="mt-1 text-xs"><Lock className="h-3 w-3 mr-1" />Locked — cannot be edited</Badge>
            )}
          </div>
        </div>

        {/* Permissions matrix */}
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="py-2 px-3 font-semibold">Module</th>
                {Object.entries(ACTION_ICONS).map(([action, { icon: Icon, label }]) => (
                  <th key={action} className="py-2 px-3 font-semibold text-center w-20">
                    <div className="flex flex-col items-center gap-0.5">
                      <Icon className="h-3.5 w-3.5" />
                      <span className="text-[10px]">{label}</span>
                    </div>
                  </th>
                ))}
                <th className="py-2 px-3 text-center w-20 text-[10px] font-semibold text-muted-foreground uppercase">All</th>
              </tr>
            </thead>
            <tbody>
              {data?.modules?.map(module => {
                const perms = rolePerms[module] || {};
                const allOn = perms.view && perms.create && perms.edit && perms.delete;
                return (
                  <tr key={module} className="border-t hover:bg-muted/20" data-testid={`perm-row-${module}`}>
                    <td className="py-2 px-3 font-medium">{MODULE_LABELS[module] || module}</td>
                    {Object.entries(ACTION_ICONS).map(([action]) => (
                      <td key={action} className="py-2 px-3 text-center">
                        <Checkbox
                          checked={!!perms[action]}
                          onCheckedChange={() => togglePermission(selectedRole, module, action)}
                          disabled={isLocked}
                          data-testid={`perm-${module}-${action}`}
                        />
                      </td>
                    ))}
                    <td className="py-2 px-3 text-center">
                      <Checkbox
                        checked={!!allOn}
                        onCheckedChange={(c) => toggleEntireRow(selectedRole, module, !!c)}
                        disabled={isLocked}
                        data-testid={`perm-${module}-all`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-muted-foreground italic">
          Note: turning OFF <strong>View</strong> automatically turns off Create/Edit/Delete. Turning ON Create/Edit/Delete automatically turns ON View.
          The <strong>Owner Admin</strong> role is locked and always has full access.
        </p>
      </CardContent>
    </Card>
  );
}
